import { useState, useEffect, useCallback, useMemo } from 'react';
import { Badge, Button, Card, ConfirmModal, Modal } from '@safetyhub/ui';
import {
  organizationsApi,
  approvalLineMetaApi,
  siteContactsApi,
  type OrganizationMember,
  type SiteContact,
  type UpdateSiteApprovalLineMetaDto,
} from '../../../../lib/organizations';
import { isWithdrawnMember } from '../../../../lib/memberStatus';
import { apiErrorMessage } from '../../../../lib/api';
import { deriveTemplateApprovalPolicy, isAssignmentSourceAllowedAtSlot } from '@safetyhub/shared';
import type {
  ApprovalLineMetaRow,
  AssignmentRoleCode,
  AssignmentSource,
  ReportTemplate,
  SiteApprovalLineMetaData,
} from '@safetyhub/shared';
import { listEnabledTemplates } from '../../../../lib/report-composer';
import { useToastStore } from '../../../../stores/toast';
import { useEntityChanged } from '../../../../hooks/useEntityChanged';
import { SearchableSelect } from '../../../../components/inspection/shared/SearchableSelect';
import { DirtyHint } from '../../../../components/common/DirtyHint';

// 현장 결재선의 축 값(templateKey)은 Composer 템플릿 key다(lowercase-hyphen, 예: 'safety-management-status-report').
// 선택지는 공개(enabled) 템플릿 목록에서만 파생하고 기본 선택은 그 첫 항목이다. DB에 구 축 값
// (UPPER_SNAKE)의 override 행이 남아 있어도 여기 목록에는 뜨지 않는다.
// 결정: docs/decisions/2026-07-24-composed-approval-site-axis.md

// 역할 기반 AssignmentSource의 **표시 라벨** — 저장된 값을 읽는 데 쓰므로 전 종류를 담는다.
// 소스 이름이지 칸 라벨이 아니다. SAFETY_MANAGER는 이 현장에 배정된 조직 구성원(안전관리자·대표,
// 주 담당 우선)으로 해소되므로 배정 탭과 같은 말 "점검 담당자"로 부른다(2026-08-31 S14 개정).
const ROLE_LABELS: Record<AssignmentRoleCode, string> = {
  SAFETY_MANAGER: '점검 담당자',
  OWNER: '대표/소유자',
  SITE_CONTACT_BY_SITE: '현장담당자',
};

/**
 * **고를 수 있는** 역할 — `SITE_CONTACT_BY_SITE`는 뺐다(2026-08-26 S76-A).
 *
 * `SiteContact`에 주/부 담당 축이 스키마에 없어 "주 담당이 들어간다"는 계약을 지킬 수 없고,
 * 저장 실적도 0건이었다. **enum 값과 서버 해소기는 남긴다** — 남아 있을 수 있는 저장분을
 * 읽지 못하게 되는 편이 더 나쁘다(그래서 위 라벨 표는 전 종류를 유지한다).
 * ⚠️ 주/부 축이 생기면 이 목록에 되살린다.
 */
const SELECTABLE_ROLES: AssignmentRoleCode[] = ['SAFETY_MANAGER', 'OWNER'];

// AssignmentSource를 UI 문자열로 변환
function describeAssignmentSource(
  src: AssignmentSource,
  members: OrganizationMember[],
  contacts: SiteContact[],
): string {
  if (src.type === 'author') return '작성자';
  if (src.type === 'delegatedTo') return '위임받은 담당자';
  if (src.type === 'role') {
    return `역할: ${ROLE_LABELS[src.role] ?? src.role}`;
  }
  if (src.type === 'member') {
    const orgMember = members.find(
      (m) => m.member?.id === src.memberId || m.memberId === src.memberId,
    );
    if (orgMember) {
      return `${orgMember.member?.name ?? '-'}`;
    }
    const contact = contacts.find((c) => c.memberId === src.memberId);
    if (contact) return `${contact.name}`;
    return `구성원 (${src.memberId.slice(0, 8)}…)`;
  }
  return '-';
}

/**
 * 편집 중인 결재 단계 한 줄.
 *
 * `chain`은 그 칸의 담당자 소스 **체인 전체**다(폴백 사다리 포함). 종전에는 첫 항목 하나
 * (`source: AssignmentSource`)만 들고 있었고, 그래서 저장할 때마다 두 가지가 조용히 부서졌다
 * (2026-08-26 S76-A):
 *  - **빈 체인**(담당자 미지정 칸)이 `{type:'author'}`로 치환돼 **작성자가 들어앉았다.**
 *  - **2단 체인**(`[{delegatedTo},{author}]`)이 머리만 남아, 위임이 없을 때 작성자로
 *    내려앉던 폴백이 사라졌다.
 * DB 증언: 시스템 층엔 2단 체인이 있는데 현장 오버라이드 42건이 전부 1단 이하였고
 * `role`·`delegatedTo` 머리는 0건이었다.
 *
 * 편집기는 단계당 소스 하나만 고를 수 있으므로, **사용자가 실제로 고르면** 체인은 그 한
 * 항목으로 교체된다(단일 소스 picker의 정직한 해석). 손대지 않으면 그대로 보존된다.
 */
type EditingRow = {
  tempId: string;
  label: string;
  locked: boolean;
  chain: AssignmentSource[];
  /**
   * 이 칸에 지정할 수 있는 소스 종류(미지정 = 제한 없음). 양식(system/템플릿) 단독 정의에서
   * 파생한다 — 서버 `enforceLockedFromSystem`이 위반 값을 **조용히 되돌리므로**, 화면이
   * 이것을 모르면 사용자는 "저장은 됐는데 값이 안 바뀐다"를 겪는다.
   */
  allowedTypes?: AssignmentSource['type'][];
};

/**
 * 서버 `effectiveAssigneeWhitelist`의 화면판 — 명시 whitelist가 있으면 그것, 없으면
 * **system이 `author`로만 정의한 칸은 `['author']`**로 간주한다.
 *
 * 두 벌로 나뉘어 있으면 한쪽만 고쳐지므로 규칙을 그대로 옮겨 적고 근거를 남긴다
 * (`apps/server/src/approval-line-meta/approval-line-locked.util.ts`).
 */
function effectiveAllowedTypes(
  row: ApprovalLineMetaRow | undefined,
): AssignmentSource['type'][] | undefined {
  if (!row) return undefined;
  if (row.assigneeSourceWhitelist) return row.assigneeSourceWhitelist;
  const chain = row.assignmentChain ?? [];
  if (chain.length > 0 && chain.every((s) => s.type === 'author')) return ['author'];
  return undefined;
}

/** @internal 왕복 보존(빈 체인·2단 체인)을 spec이 직접 재도록 export한다. */
export function makeEditingRows(
  lines: ApprovalLineMetaRow[],
  /** 양식(system/템플릿) 단독 정의 — 담당자 제한의 권위. 없으면 제한 없음. */
  baseLines: ApprovalLineMetaRow[] = [],
): EditingRow[] {
  return lines.map((l, i) => ({
    tempId: `row-${i}-${Date.now()}`,
    label: l.label,
    locked: l.locked,
    // ⚠️ 빈 체인은 **빈 채로** 둔다 — 여기서 작성자를 지어 넣으면 미지정 칸이 사라진다.
    chain: l.assignmentChain ?? [],
    allowedTypes: effectiveAllowedTypes(baseLines[i]),
  }));
}

/** @internal `makeEditingRows`와 짝. 둘의 왕복이 손실 없음을 spec이 단언한다. */
export function toApprovalLineMetaRows(rows: EditingRow[]): ApprovalLineMetaRow[] {
  return rows.map((r, i) => ({
    level: i + 1,
    label: r.label,
    locked: r.locked,
    // 체인 **전체**를 그대로 되돌려 쓴다(머리만 쓰면 폴백 사다리가 잘린다).
    assignmentChain: r.chain,
  }));
}

// ── 무변경 저장 게이트용 내용 비교 ──
// tempId는 편집 세션마다 랜덤이므로 제외하고, 저장 payload에 실리는 축(label·locked·chain)만 본다.
function isSameSource(a: AssignmentSource, b: AssignmentSource): boolean {
  if (a.type !== b.type) return false;
  if (a.type === 'role' && b.type === 'role') return a.role === b.role;
  if (a.type === 'member' && b.type === 'member') return a.memberId === b.memberId;
  return true;
}

function isSameChain(a: AssignmentSource[], b: AssignmentSource[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((src, i) => isSameSource(src, b[i]));
}

function isSameEditingRows(a: EditingRow[], b: EditingRow[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (row, i) =>
      row.label === b[i].label && row.locked === b[i].locked && isSameChain(row.chain, b[i].chain),
  );
}

// 상속 레코드 결정: 현장 override > 양식(템플릿) 기본값.
// 서버 cascade(`ReportComposerService.resolveComposedApprovalMeta`)와 같은 두 층이다(조직 override
// 층은 없다 — admin SiteApprovalLineTab과 동형).
function resolveEffectiveMeta(
  templateKey: string,
  siteOverrides: SiteApprovalLineMetaData[],
  templateDefaults: SiteApprovalLineMetaData[],
): { record: SiteApprovalLineMetaData | null; source: 'site' | 'template' | null } {
  const site = siteOverrides.find((r) => r.templateKey === templateKey);
  if (site) return { record: site, source: 'site' };
  const tpl = templateDefaults.find((r) => r.templateKey === templateKey);
  if (tpl) return { record: tpl, source: 'template' };
  return { record: null, source: null };
}

// Composer 템플릿을 마지막 층(system 층) 레코드로 환원한다 — 템플릿 approvalLineMeta가 그 역할을 한다
// (결정 3 "템플릿이 system 층"). 구조·한도·활성 여부는 이 층 단독 권위다.
// 파생(정책 유무 = 고정 단계 존재 · 한도 `?? 0` · base 비-locked 수)은 shared `deriveTemplateApprovalPolicy`
// 하나가 소유한다 — 서버 `resolveSystemLayer`·report-composer·admin과 같은 함수라 화면에서만 "정책 있음"으로
// 갈리는 일이 없다(라운드 6에서 5벌을 수렴).
function templatesAsSystemDefaults(
  templates: ReportTemplate[],
): SiteApprovalLineMetaData[] {
  return templates.map((t) => {
    const policy = deriveTemplateApprovalPolicy(t.approvalLineMeta);
    return {
      templateKey: t.key,
      defaultApprovalLine: policy.base,
      maxAdditionalLines: policy.maxAdditionalLines,
      hasApprovalLinePolicy: policy.hasApprovalLinePolicy,
      baseNonLockedCount: policy.baseNonLockedCount,
      version: 0,
    };
  });
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
  site: '현장 override',
  template: '양식 기본값 (상속)',
};

// ──────────────────────────────────────────────
// 단일 편집 행 컴포넌트
// ──────────────────────────────────────────────

interface EditRowProps {
  row: EditingRow;
  index: number;
  total: number;
  members: OrganizationMember[];
  contacts: SiteContact[];
  isAddingContact: boolean;
  onChangeLabel: (val: string) => void;
  /** 담당자 소스 교체(체인을 이 소스 하나로) */
  onChangeSource: (src: AssignmentSource) => void;
  /** 담당자 미지정으로 되돌리기(빈 체인) — 지정된 칸을 비우는 유일한 수단 */
  onClearSource: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onAddContactInline: (rowTempId: string) => void;
}

function LockedIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function EditRow({
  row,
  index,
  total,
  members,
  contacts,
  onChangeLabel,
  onChangeSource,
  onClearSource,
  onMoveUp,
  onMoveDown,
  onRemove,
  onAddContactInline,
}: EditRowProps) {
  // `locked`는 **칸 구조**(존재·순서·라벨)에 대한 잠금이지 담당자에 대한 것이 아니다
  // (결정 2026-08-13 결재란/담당자 분리 §7). 서버도 보고서 편집 모달도 이미 그 모델인데
  // 이 화면만 2026-06-09 모델(행 통째 읽기 전용)에 남아 있었다 — 담당자 select는 열고
  // 구조(라벨 입력·삭제·이동)만 잠근다(2026-08-26 S76-A).
  const isLocked = row.locked;
  const src = row.chain[0];
  // 이 칸에 지정할 수 있는 소스 종류(미지정 = 제한 없음). 서버가 위반 값을 **조용히**
  // 되돌리므로, 허용되지 않는 선택지는 아예 내놓지 않는다.
  //
  // S77(결정 `2026-08-27-author-delegated-sources-first-slot-only.md`): 첫 칸이 아니면
  // `작성자`·`위임받은 담당자`도 뺀다. 뒤 칸에 두면 작성자가 서명하는 순간(또는 위임
  // 대상자가 제출하는 순간) 자동으로 채워져 **제3자 결재를 한 번도 거치지 않는다.**
  // 판정은 서버와 같은 공유 술어(`isAssignmentSourceAllowedAtSlot`)에 맡긴다 — 두 벌이면
  // 화면만 막고 서버는 통과하는 어긋남이 생긴다.
  //
  // ⚠️ 이 목록은 `role`을 막지 않는다. 뒤 칸의 `{role: 안전관리자}`가 우연히 작성자
  // 본인으로 해소되는 것은 정상 흐름이다(S12·S20-b·S20-c).
  const allows = (type: AssignmentSource['type']) =>
    (!row.allowedTypes || row.allowedTypes.includes(type)) &&
    isAssignmentSourceAllowedAtSlot(type, index);
  /** 저장된 값이 `작성자`·`위임받은 담당자`라 선택지에서 빠졌어도 살려 둬야 하는가(아래 ⚠️). */
  const showsSpecial = src?.type === 'author' || src?.type === 'delegatedTo';

  // 유형 선택 드롭다운 값. 빈 체인(담당자 미지정)은 ''.
  const typeValue: string = !src
    ? ''
    : src.type === 'author'
      ? '__author__'
      : src.type === 'delegatedTo'
        ? '__delegatedTo__'
        : src.type === 'role'
          ? `__role__${src.role}`
          : `__member__${src.memberId}`;

  // 저장된 값이 선택지에서 감춰진 역할일 수 있다(SITE_CONTACT_BY_SITE). 그 경우에만
  // 그 항목을 살려 둔다 — 아니면 select가 빈칸으로 보여 "지정이 사라진 것"처럼 읽힌다.
  const roleOptions: AssignmentRoleCode[] =
    src?.type === 'role' && !SELECTABLE_ROLES.includes(src.role)
      ? [...SELECTABLE_ROLES, src.role]
      : SELECTABLE_ROLES;

  const handleTypeChange = (val: string) => {
    if (val === '') {
      // "담당자 미지정" — 지정된 칸을 **비우는 유일한 수단**이다. 종전에는 이 값이 아무
      // 분기에도 걸리지 않아 선택해도 아무 일도 일어나지 않았다.
      onClearSource();
    } else if (val === '__author__') {
      onChangeSource({ type: 'author' });
    } else if (val === '__delegatedTo__') {
      onChangeSource({ type: 'delegatedTo' });
    } else if (val.startsWith('__role__')) {
      onChangeSource({ type: 'role', role: val.replace('__role__', '') as AssignmentRoleCode });
    } else if (val.startsWith('__member__')) {
      onChangeSource({ type: 'member', memberId: val.replace('__member__', '') });
    }
  };

  return (
    <div
      className={
        isLocked
          ? 'flex items-start gap-2 p-3 bg-ink-50 rounded-card'
          : 'flex items-start gap-2 p-3 bg-brand-50 rounded-card'
      }
    >
      {/* 순번 */}
      <span
        className={
          isLocked
            ? 'inline-flex items-center justify-center h-6 w-6 rounded-full bg-ink-200 text-ink-600 text-xs font-medium flex-shrink-0 mt-1'
            : 'inline-flex items-center justify-center h-6 w-6 rounded-full bg-brand-200 text-brand-800 text-xs font-medium flex-shrink-0 mt-1'
        }
      >
        {index + 1}
      </span>

      {/* 편집 필드 */}
      <div className="flex-1 min-w-0 space-y-2">
        {/* 단계명 — locked 칸은 구조가 admin 권위라 읽기 전용 */}
        {isLocked ? (
          <div className="flex items-center gap-1.5">
            <LockedIcon className="w-3.5 h-3.5 text-ink-400 flex-shrink-0" />
            <span className="text-xs font-medium text-ink-700 truncate">
              {row.label || '(단계명 없음)'}
            </span>
            <Badge tone="slate" shape="rounded" size="sm" className="flex-shrink-0">
              관리자 고정
            </Badge>
          </div>
        ) : (
          <input
            type="text"
            value={row.label}
            onChange={(e) => onChangeLabel(e.target.value)}
            placeholder="단계명 (예: 소장, 대표)"
            className="w-full text-xs border border-ink-300 rounded-control px-2 py-1 focus:ring-brand-500 focus:border-brand-500"
          />
        )}

        {/* 배정 소스 선택 — locked 칸에서도 열려 있다(담당자는 잠금 대상이 아니다) */}
        <select
          value={typeValue}
          onChange={(e) => handleTypeChange(e.target.value)}
          className="w-full text-xs border border-ink-300 rounded-control px-2 py-1 focus:ring-brand-500 focus:border-brand-500"
        >
          {/* 비우기 = 담당자 미지정 칸(종이로 결재). 항상 허용된다 — 제한은 "누구를 세울 수
              있나"이지 "반드시 세워야 하나"가 아니다. */}
          <option value="">담당자 미지정 (수기 결재)</option>
          {/* ⚠️ `allows(...) || src?.type === ...` — 아래 두 optgroup과 같은 규약이다.
              whitelist(`row.allowedTypes`)는 admin이 나중에 좁힐 수 있어, 이미 저장된 값의
              종류가 whitelist 밖으로 밀려날 수 있다. 그때 optgroup을 통째로 감추면
              **select가 빈칸**이 되어 "지정이 사라진 것"처럼 읽히고, 정작 그 칸을 고치려는
              사람에게 현재 값이 보이지 않는다(저장 자체는 서버가 판정한다). */}
          {(allows('role') || src?.type === 'role') && (
            <optgroup label={allows('role') ? '역할 기반' : '역할 기반 (관리자 지정 밖 — 다른 값으로 바꿔 주세요)'}>
              {roleOptions.map((role) => (
                <option key={role} value={`__role__${role}`}>
                  역할: {ROLE_LABELS[role] ?? role}
                </option>
              ))}
            </optgroup>
          )}
          {/* ⚠️ **이미 저장된 값**은 선택지에서 빠졌어도 살려 둔다 — 아니면 select가 빈칸으로
              보여 "지정이 사라진 것"처럼 읽힌다(감춰진 역할과 같은 규약). S77 이전에 뒤 칸에
              박힌 `작성자`·`위임받은 담당자`가 실제로 남아 있고(prod 1행), 그 칸을 고치려면
              현재 값이 보여야 한다. 다른 값을 고르는 순간 이 항목은 사라진다. */}
          {(allows('author') || allows('delegatedTo') || showsSpecial) && (
            <optgroup label="특수">
              {(allows('author') || src?.type === 'author') && (
                <option value="__author__">
                  작성자{!allows('author') ? ' (첫 결재란만 — 다른 값으로 바꿔 주세요)' : ''}
                </option>
              )}
              {(allows('delegatedTo') || src?.type === 'delegatedTo') && (
                <option value="__delegatedTo__">
                  위임받은 담당자
                  {!allows('delegatedTo') ? ' (첫 결재란만 — 다른 값으로 바꿔 주세요)' : ''}
                </option>
              )}
            </optgroup>
          )}
          {(allows('member') || src?.type === 'member') && members.length > 0 && (
            <optgroup label={allows('member') ? '조직 멤버' : '조직 멤버 (관리자 지정 밖 — 다른 값으로 바꿔 주세요)'}>
              {/* S17-c: 탈퇴한 계정은 후보에서 뺀다 — 보고서 편집 모달(`ApprovalLineSectionImpl.
                  filteredMembers`)과 같은 술어. 계정 없는 인원은 남는다. `members` state 자체는
                  거르지 않는다 — 기존 체인의 이름 표시(describeAssignmentSource)가 그 목록을 읽는다. */}
              {members.filter((m) => !isWithdrawnMember(m)).map((m) => {
                const memberId = m.member?.id ?? m.memberId ?? '';
                const name = m.member?.name ?? '-';
                return (
                  <option key={memberId} value={`__member__${memberId}`}>
                    {name}
                  </option>
                );
              })}
            </optgroup>
          )}
          {(allows('member') || src?.type === 'member') && contacts.length > 0 && (
            <optgroup label={allows('member') ? '현장 담당자' : '현장 담당자 (관리자 지정 밖 — 다른 값으로 바꿔 주세요)'}>
              {contacts.map((c) => (
                <option key={c.memberId} value={`__member__${c.memberId}`}>
                  {c.name}{c.position ? ` (${c.position})` : ''}
                </option>
              ))}
            </optgroup>
          )}
        </select>

        {/* 2단 이상 폴백 사다리는 select 하나로 표현되지 않는다 — 손대지 않으면 그대로
            보존되지만, 무엇이 보존 중인지는 보여 준다(고르면 그 하나로 교체된다). */}
        {row.chain.length > 1 && (
          <p className="text-xs text-ink-400">
            현재:{' '}
            {row.chain.map((s) => describeAssignmentSource(s, members, contacts)).join(' → ')}
            {' '}(앞이 없으면 뒤로 넘어감 — 새로 고르면 하나로 교체됩니다)
          </p>
        )}

        {/* 외부 담당자 inline 추가 버튼 */}
        {allows('member') && (
          <button
            type="button"
            onClick={() => onAddContactInline(row.tempId)}
            className="text-xs text-brand-600 hover:text-brand-800 underline"
          >
            + 현장 담당자 추가
          </button>
        )}
      </div>

      {/* 이동/삭제 버튼 — 구조 변경이라 locked 칸에서는 비활성 */}
      <div
        className={
          isLocked
            ? 'flex flex-col gap-0.5 flex-shrink-0 mt-1 opacity-20 cursor-not-allowed'
            : 'flex flex-col gap-0.5 flex-shrink-0 mt-1'
        }
      >
        <button
          onClick={onMoveUp}
          disabled={isLocked || index === 0}
          className="p-1 text-ink-400 hover:text-ink-600 disabled:opacity-30"
          title="위로"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
        <button
          onClick={onMoveDown}
          disabled={isLocked || index === total - 1}
          className="p-1 text-ink-400 hover:text-ink-600 disabled:opacity-30"
          title="아래로"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <button
          onClick={onRemove}
          disabled={isLocked}
          className="p-1 text-red-400 hover:text-red-600 disabled:opacity-30"
          title="삭제"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// 외부 담당자 inline 생성 모달
// ──────────────────────────────────────────────

interface AddContactModalProps {
  companyId: string;
  siteId: string;
  onCreated: (contact: SiteContact) => void;
  onClose: () => void;
}

function AddContactModal({ companyId, siteId, onCreated, onClose }: AddContactModalProps) {
  const { showToast } = useToastStore();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !phone.trim()) {
      showToast('이름과 전화번호를 입력하세요.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const created = await siteContactsApi.create(companyId, siteId, {
        name: name.trim(),
        phone: phone.trim(),
      });
      onCreated(created);
    } catch {
      showToast('담당자 추가에 실패했습니다.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="현장 담당자 추가"
      size="sm"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            취소
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? '저장 중...' : '추가'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-ink-600 mb-1">이름 *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="홍길동"
            className="w-full text-sm border border-ink-300 rounded-control px-2 py-1.5 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
        <div>
          <label className="block text-xs text-ink-600 mb-1">전화번호 *</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="010-0000-0000"
            className="w-full text-sm border border-ink-300 rounded-control px-2 py-1.5 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
      </div>
    </Modal>
  );
}

// ──────────────────────────────────────────────
// 메인 섹션 컴포넌트
// ──────────────────────────────────────────────

interface Props {
  companyId: string;
  siteId: string;
  /**
   * 현장 결재선 편집 입구 — 값은 **권한 키 `approval.line.manage`**다(호출부
   * `SiteDetailDesktop`·`SiteDetailMobile`의 `usePermissions().can('approval.line.manage')`).
   *
   * ⚠️ **소유권 성질(`isOwner`)이 아니다.** 2026-09-26까지 이 prop 이름이 `isOwner`였는데 값은 이미 키였고,
   * 그래서 「이름대로 정리」하면(`usePermissions().isOwner`로 바꾸면) `approval.line.manage`만 가진 시드
   * 안전관리자가 **편집 입구를 잃는다** — 서버는 허용하는데 화면에 길이 없는 상태로, 2026-09-24에 그
   * 유형의 결함을 이미 한 번 고쳤다. 컴포넌트 spec은 이 prop을 직접 주입하므로 그 회귀에 전부 초록이다.
   *
   * 서버 정본: `approval-line-meta.controller`의 `@Permissions('approval.line.manage')`.
   * 배선 핀: `components/__tests__/rbacOwnerAxisWiringGuard.test.ts`.
   */
  canManageApprovalLines: boolean;
}

export function SiteApprovalLinesSection({ companyId, siteId, canManageApprovalLines }: Props) {
  const { showToast } = useToastStore();

  // 선택된 축 값(templateKey) — Composer 템플릿 key. 템플릿 목록이 오기 전에는 빈 값이고,
  // 목록이 오면 아래 effect가 첫 템플릿으로 채운다(하드코딩 기본값은 그 양식이 은퇴할 때 조용히 빈 탭이 된다).
  const [selectedType, setSelectedType] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);


  // 현장 override 행 (응답의 유일한 층 — resolveEffectiveMeta 주석)
  const [siteOverrides, setSiteOverrides] = useState<SiteApprovalLineMetaData[]>([]);
  // 공개(enabled) Composer 템플릿 — 선택지·마지막 층의 정본. 실패하면 배너로 알린다.
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [templatesFailed, setTemplatesFailed] = useState(false);

  // 템플릿을 마지막 층(system 층)으로 환원한 레코드
  const templateDefaults = useMemo(
    () => templatesAsSystemDefaults(templates),
    [templates],
  );

  // 축 값 → 표시 이름. 목록에 없는 key(재조회 실패로 목록이 비었을 때 등)는 raw key로 떨어진다.
  const labelOf = useCallback(
    (key: string): string => templates.find((t) => t.key === key)?.name ?? key,
    [templates],
  );

  // 보고서 양식 콤보박스 옵션 — 공개 Composer 양식. "현장" 배지(★)는 이름에 포함하여 현장 override 존재를 표시.
  const reportTypeOptions = useMemo(
    () =>
      templates.map((t) => {
        const hasSiteRow = siteOverrides.some((r) => r.templateKey === t.key);
        return { id: t.key, name: `${t.name}${hasSiteRow ? ' ★' : ''}` };
      }),
    [siteOverrides, templates],
  );

  // 멤버/담당자 후보 목록
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [contacts, setContacts] = useState<SiteContact[]>([]);

  // 편집 상태
  const [isEditing, setIsEditing] = useState(false);
  const [editingRows, setEditingRows] = useState<EditingRow[]>([]);
  // 편집 시작 시점 스냅샷 — 무변경 저장(가짜 변경 이력·version 증가)을 막는 dirty 판정 기준.
  // 편집 취소/탭 전환/현장 전환 시 비우고, 재진입 시 그 시점 값으로 다시 찍는다.
  const [editSnapshot, setEditSnapshot] = useState<EditingRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  // 되돌리기 확인 다이얼로그
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // 외부 담당자 inline 생성 모달 — targetRowTempId: 생성 후 해당 행에 자동 배정
  const [addContactForRow, setAddContactForRow] = useState<string | null>(null);

  // silent: 실시간 재조회(ENTITY_CHANGED) 경로 — 섹션을 스피너로 갈아 끼우지 않고 값만 새로 채운다.
  const loadData = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!silent) setIsLoading(true);
    try {
      const [metaRes, membersRes, contactsRes] = await Promise.all([
        approvalLineMetaApi.getSiteMeta(siteId),
        organizationsApi.getMembers(companyId),
        siteContactsApi.findAll(companyId, siteId),
      ]);
      setSiteOverrides(metaRes.siteOverrides);
      setMembers(membersRes);
      setContacts(contactsRes);
    } catch {
      // silent 재조회(실시간 이벤트)의 실패는 알리지 않는다 — 사용자가 아무것도 하지 않았는데
      // 에러 토스트가 뜨면 자기 행동이 실패한 줄 안다. 다음 이벤트·다음 진입에서 복구된다.
      if (!silent) showToast('결재선 데이터를 불러오지 못했습니다.', 'error');
    } finally {
      setIsLoading(false);
    }

    // 템플릿 목록은 별도 try — 실패해도 이미 로드된 화면(멤버·현장 override)은 살아 있어야 한다.
    try {
      setTemplates(await listEnabledTemplates());
      setTemplatesFailed(false);
    } catch {
      // ⚠️ 이전 목록을 **지우지 않는다**. 지우면 선택 중이던 축의 라벨이 raw key가 되고 템플릿 층이
      // cascade에서 빠진다 → 상속값이 빈 결재선으로, 한도가 0으로 보이고, 그 상태로 저장하면
      // 템플릿 key에 **빈 override가 기록된다.**
      // (저장·되돌리기 후 loadData 재실행 중 이 fetch만 실패하면 실제로 도달하는 상태다.)
      // 첫 로드 실패면 애초에 비어 있어 축을 고를 수 없으므로(선택지 없음 안내) 위험이 없다.
      setTemplatesFailed(true);
    }
  }, [companyId, siteId, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // loadData가 읽는 세 재료(현장 결재선 override·조직 멤버·현장 담당자)의 변경을 조용히 반영.
  // 편집 중(isEditing)이면 미루고 저장·취소로 편집이 끝날 때 1회 재조회 — 편집 행을 덮어쓰지 않는다.
  useEntityChanged(
    ['site-approval-line-meta', 'member', 'site-contact'],
    () => loadData({ silent: true }),
    { paused: isEditing },
  );

  // 현장이 바뀌면 선택/편집 상태를 초기화한다.
  // 이 컴포넌트는 현장 간 이동에서 언마운트되지 않으므로, 초기화하지 않으면 이전 현장에서
  // 편집하던 행이 새 현장의 축으로 저장된다. 선택은 비우고 아래 effect가 첫 템플릿으로 되돌린다.
  useEffect(() => {
    setSelectedType('');
    setIsEditing(false);
    setEditingRows([]);
    setEditSnapshot([]);
  }, [siteId]);

  // 기본 선택 = 공개 템플릿 첫 항목. 선택이 비었거나(초기·현장 전환) 목록에 없는 key면 첫 항목으로.
  // 재조회에서 템플릿 fetch만 실패한 경우는 목록을 지우지 않으므로(위 loadData) 선택이 유지된다.
  useEffect(() => {
    if (templates.length === 0) return;
    if (selectedType && templates.some((t) => t.key === selectedType)) return;
    setSelectedType(templates[0].key);
  }, [templates, selectedType]);

  // 탭 변경 시 편집 초기화
  const handleTabChange = (type: string) => {
    setSelectedType(type);
    setIsEditing(false);
    setEditingRows([]);
    setEditSnapshot([]);
  };

  const selectedLabel = labelOf(selectedType);

  const sourceLabelOf = (src: string): string => SOURCE_TYPE_LABELS[src] ?? src;

  // 현재 탭의 effective 메타 — 현장 override > 양식(템플릿) 기본값
  const { record: effectiveRecord, source: effectiveSource } = resolveEffectiveMeta(
    selectedType,
    siteOverrides,
    templateDefaults,
  );

  /**
   * 선택 탭의 **양식(템플릿) 단독** 정책 — 저작자 권위라 현장 override 값을 무시한다.
   * - `maxAdditionalLines`: 현장이 늘릴 수 없다(안내용 표시만).
   * - `baseNonLockedCount`: 한도 판정의 base — 서버(`setReportApprovalLines` §5) 판정식
   *   「비-locked 총수 ≤ 이 값 + maxAdditionalLines」. 양식이 정의한 칸은 한도를 쓰지 않는다.
   *   ⚠️ 편집 중인 행(현장 병합본)으로 세면 현장이 자기 칸을 넣어 base를 부풀린다.
   * - `base`: 담당자 제한(whitelist)의 권위 — 병합본으로 파생하면 현장이 스스로 제한을 푼다.
   * 템플릿 레코드는 여기서 한 번만 찾는다.
   */
  const selectedTemplatePolicy = useMemo(
    () => deriveTemplateApprovalPolicy(templates.find((t) => t.key === selectedType)?.approvalLineMeta),
    [templates, selectedType],
  );
  const adminMaxAdditionalLines = selectedTemplatePolicy.maxAdditionalLines;
  const baseNonLockedCount = selectedTemplatePolicy.baseNonLockedCount;

  const hasSiteOverride = siteOverrides.some((r) => r.templateKey === selectedType);

  // 편집 시작: site override 있으면 그 값, 없으면 상속값(양식 기본값)을 초기값으로
  const handleStartEdit = () => {
    const initialLines = effectiveRecord?.defaultApprovalLine ?? [];
    const rows = makeEditingRows(initialLines, selectedTemplatePolicy.base);
    setEditingRows(rows);
    setEditSnapshot(rows);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingRows([]);
    setEditSnapshot([]);
  };

  // 현장 override 삭제 → 양식 기본값 상속으로 되돌리기
  const handleReset = async () => {
    setIsResetting(true);
    try {
      await approvalLineMetaApi.deleteSiteMeta(siteId, selectedType);
      await loadData();
      setShowResetConfirm(false);
      showToast('현장 결재선이 기본값으로 되돌려졌습니다.', 'success');
    } catch {
      showToast('되돌리기에 실패했습니다.', 'error');
    } finally {
      setIsResetting(false);
    }
  };

  // 서버 판정과 같은 축: 비-locked 총수 ≤ **양식 단독** 비-locked 칸 수 + maxAdditionalLines.
  // 양식이 정의한 칸(고정·편집 허용 모두)은 한도를 쓰지 않으므로, 현장이 실제로 더 만들 수
  // 있는 칸은 그 위의 maxAdditionalLines개다. 종전(모델 B)은 비-locked 전체가 한도를 소비해,
  // 양식이 편집 허용 칸을 준 경우 현장이 그 칸만으로 이미 한도에 걸렸다.
  const nonLockedEditingCount = editingRows.filter((r) => !r.locked).length;
  const isAtMaxAdditional =
    nonLockedEditingCount >= baseNonLockedCount + adminMaxAdditionalLines;

  // 무변경 저장 게이트 — 기존 site override 수정 시에만 적용한다.
  // override가 아직 없으면(상속값으로 편집 시작) 내용이 같아도 저장이 "현장 override 행 생성"
  // 이라는 실효를 가지므로(편집 모드 안내 문구가 그 흐름을 그대로 안내한다) 막지 않는다.
  const isDirty = !hasSiteOverride || !isSameEditingRows(editingRows, editSnapshot);

  const handleAddRow = () => {
    // 한도 도달 시 추가 차단
    if (isAtMaxAdditional) return;
    // 새로 추가하는 단계는 항상 unlocked (사용자가 자유 편집 가능)
    setEditingRows((prev) => [
      ...prev,
      {
        tempId: `row-new-${Date.now()}`,
        label: '',
        locked: false,
        // 새 칸은 **담당자 미지정**으로 시작한다 — 작성자를 기본으로 박으면 사용자가 고르지도
        // 않은 지정이 저장된다(칸 추가와 담당자 지정은 별개 축, 결정 2026-08-13).
        chain: [],
      },
    ]);
  };

  const handleRemoveRow = (index: number) => {
    // locked 단계 삭제 차단
    setEditingRows((prev) => {
      if (prev[index]?.locked) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    setEditingRows((prev) => {
      // locked 단계는 이동 불가, 또한 locked 단계와의 swap도 차단
      if (prev[index]?.locked || prev[index - 1]?.locked) return prev;
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const handleMoveDown = (index: number) => {
    setEditingRows((prev) => {
      if (index === prev.length - 1) return prev;
      // locked 단계는 이동 불가, 또한 locked 단계와의 swap도 차단
      if (prev[index]?.locked || prev[index + 1]?.locked) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  const handleChangeLabel = (index: number, val: string) => {
    setEditingRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, label: val } : r)),
    );
  };

  // 담당자 소스 교체 — 편집기는 단계당 소스 하나만 고를 수 있으므로 체인을 그 하나로 바꾼다.
  // (손대지 않은 칸은 이 경로를 타지 않아 폴백 사다리가 그대로 보존된다.)
  const handleChangeSource = (index: number, src: AssignmentSource) => {
    setEditingRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, chain: [src] } : r)),
    );
  };

  // 담당자 미지정으로 되돌리기 — 빈 체인. locked 칸에도 허용된다(담당자는 잠금 대상이 아니고,
  // 미지정 칸이 오프라인 수기 결재로 동작하는 것이 이 설계의 핵심이다).
  const handleClearSource = (index: number) => {
    setEditingRows((prev) => prev.map((r, i) => (i === index ? { ...r, chain: [] } : r)));
  };

  // 외부 담당자 inline 생성 완료: contacts 갱신 + 해당 행에 자동 배정
  const handleContactCreated = (contact: SiteContact, targetRowTempId: string) => {
    setContacts((prev) => [...prev, contact]);
    setEditingRows((prev) =>
      prev.map((r) =>
        r.tempId === targetRowTempId
          ? { ...r, chain: [{ type: 'member', memberId: contact.memberId }] }
          : r,
      ),
    );
    setAddContactForRow(null);
    showToast(`${contact.name} 담당자가 추가되었습니다.`, 'success');
  };

  const handleSave = async () => {
    // 한도(maxAdditionalLines)·정책 유무(hasApprovalLinePolicy)는 양식(템플릿 approvalLineMeta) 단독 권위라
    // body에 싣지 않는다 — 현장 override 컬럼은 2026-09-16 R1에서 DROP됐고 서버 DTO가 whitelist라 보내면 400.
    // 화면의 한도 안내(selectedTemplatePolicy.maxAdditionalLines)는 템플릿 approvalLineMeta를 읽는 것이라 그대로다.
    const dto: UpdateSiteApprovalLineMetaDto = {
      lines: toApprovalLineMetaRows(editingRows),
      reason: '현장 결재라인 수정',
    };
    setIsSaving(true);
    try {
      await approvalLineMetaApi.updateSiteMeta(siteId, selectedType, dto);
      await loadData();
      setIsEditing(false);
      setEditingRows([]);
      showToast('결재선이 저장되었습니다.', 'success');
    } catch (err) {
      // 서버는 거부 사유를 칸까지 지목해 내려준다(한도 초과 / 앞 칸부터 순서대로).
      // 그 문구를 일반 문구로 덮으면 사용자가 어디를 고쳐야 하는지 알 수 없다(S3·S65).
      showToast(apiErrorMessage(err) ?? '결재선 저장에 실패했습니다.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
      </div>
    );
  }

  const displayLines = effectiveRecord?.defaultApprovalLine ?? [];

  return (
    <div className="space-y-4">
      {/* 보고서 양식 선택 — 검색 가능 콤보박스 */}
      <Card padding="md" radius="lg" elevation="md">
        <div className="flex items-center gap-2 mb-4">
          <label className="text-xs font-medium text-ink-600 flex-shrink-0">보고서 양식</label>
          <div className="flex-1 min-w-0">
            <SearchableSelect
              value={selectedType}
              onChange={(v) => {
                if (v) handleTabChange(v);
              }}
              options={reportTypeOptions}
              placeholder="보고서 양식 선택..."
            />
          </div>
          {hasSiteOverride && (
            <Badge tone="brand" className="flex-shrink-0">
              현장 설정 적용 중
            </Badge>
          )}
        </div>

        {/* Composer 양식 목록을 못 불러온 경우 — 선택지가 비거나(첫 로드) 이전 목록으로 degrade됨을 알린다 */}
        {templatesFailed && (
          <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-card-sm text-xs text-amber-700">
            Composer 양식 목록을 불러오지 못했습니다. 양식별 결재선을 표시할 수 없습니다.
          </div>
        )}

        {/* 선택된 양식의 결재선 — 선택할 양식이 없으면(템플릿 0건) 본문을 그리지 않는다 */}
        {selectedType && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="text-sm font-medium text-ink-900 truncate">
                {selectedLabel} 결재선
              </h3>
              {effectiveSource && (
                <span className="text-xs text-ink-400 flex-shrink-0">
                  ({sourceLabelOf(effectiveSource)})
                </span>
              )}
            </div>
            {canManageApprovalLines && !isEditing && (
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                {hasSiteOverride && (
                  <button
                    onClick={() => setShowResetConfirm(true)}
                    className="text-xs text-ink-500 hover:text-red-600 underline"
                  >
                    기본값으로 되돌리기
                  </button>
                )}
                <button
                  onClick={handleStartEdit}
                  className="text-sm text-brand-600 hover:text-brand-800"
                >
                  {hasSiteOverride ? '수정' : '현장 결재라인 설정'}
                </button>
              </div>
            )}
          </div>

          {/* 최대 추가 결재 안내 (양식 저작자 단독 권위 — 현장 변경 불가) */}
          {adminMaxAdditionalLines > 0 && (
            <Card padding="sm" radius="sm" surface="soft" elevation="none" className="mb-3 text-xs text-ink-600">
              최대 추가 결재 <span className="font-medium text-ink-800">{adminMaxAdditionalLines}개</span>{' '}
              (양식(Composer) 설정, 변경 불가)
            </Card>
          )}

          {!isEditing ? (
            /* 읽기 모드 */
            displayLines.length === 0 ? (
              <p className="text-sm text-ink-500 py-4 text-center">
                설정된 결재선이 없습니다.
                {canManageApprovalLines && (
                  <button
                    onClick={handleStartEdit}
                    className="ml-2 text-brand-600 hover:text-brand-800"
                  >
                    추가하기
                  </button>
                )}
              </p>
            ) : (
              <div className="space-y-2">
                {!hasSiteOverride && effectiveSource && effectiveSource !== 'site' && (
                  <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded-card-sm text-xs text-amber-700">
                    현재 {sourceLabelOf(effectiveSource)}이 적용 중입니다. [현장 결재라인 설정]을 클릭하면 현장 전용으로 override할 수 있습니다.
                  </div>
                )}
                {displayLines.map((line, idx) => (
                  <Card
                    key={`${line.level}-${idx}`}
                    padding="sm"
                    surface="soft"
                    elevation="none"
                    className="flex items-center"
                  >
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-brand-100 text-brand-800 text-xs font-medium mr-3 flex-shrink-0">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      {/* 단계명 · 결재자명 · 배지 한 줄 나열 */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-ink-900 flex-shrink-0">{line.label}</span>
                        <span className="text-ink-300 flex-shrink-0">·</span>
                        <span className="text-sm text-ink-600 truncate min-w-0">
                          {/* 빈 체인 = 담당자 미지정 칸(오프라인 수기 결재). 종전에는 빈
                              문자열이 되어 "설정이 비어 보이는" 것과 구별되지 않았다. */}
                          {(line.assignmentChain ?? []).length === 0
                            ? '담당자 미지정 (수기 결재)'
                            : line.assignmentChain
                                .map((src) => describeAssignmentSource(src, members, contacts))
                                .join(' → ')}
                        </span>
                        {line.locked && (
                          /* 편집 모드의 "관리자 고정" 배지와 같은 개념이라 tone도 같게 둔다 */
                          <Badge tone="slate" shape="rounded" size="sm" className="flex-shrink-0">
                            고정
                          </Badge>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )
          ) : (
            /* 편집 모드 */
            <div className="space-y-3">
              {!hasSiteOverride && effectiveSource && effectiveSource !== 'site' && (
                <div className="p-2 bg-brand-50 border border-brand-200 rounded-card-sm text-xs text-brand-700">
                  {sourceLabelOf(effectiveSource)} 값을 초기값으로 현장 override를 설정합니다.
                </div>
              )}

              {editingRows.length === 0 ? (
                <p className="text-sm text-ink-500 py-2 text-center">결재 단계를 추가하세요.</p>
              ) : (
                <div className="space-y-2">
                  {editingRows.map((row, index) => (
                    <EditRow
                      key={row.tempId}
                      row={row}
                      index={index}
                      total={editingRows.length}
                      members={members}
                      contacts={contacts}
                      isAddingContact={addContactForRow === row.tempId}
                      onChangeLabel={(val) => handleChangeLabel(index, val)}
                      onChangeSource={(src) => handleChangeSource(index, src)}
                      onClearSource={() => handleClearSource(index)}
                      onMoveUp={() => handleMoveUp(index)}
                      onMoveDown={() => handleMoveDown(index)}
                      onRemove={() => handleRemoveRow(index)}
                      onAddContactInline={(rowTempId) => setAddContactForRow(rowTempId)}
                    />
                  ))}
                </div>
              )}

              {/* 단계 추가 버튼 — 양식 단독 비-locked 칸 + 한도에 도달하면 비활성 */}
              <button
                onClick={handleAddRow}
                disabled={isAtMaxAdditional}
                className="w-full py-2 text-sm text-brand-600 border border-dashed border-brand-300 rounded-control hover:bg-brand-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                {isAtMaxAdditional
                  ? `최대 추가 결재 ${adminMaxAdditionalLines}개에 도달했습니다`
                  : '+ 결재 단계 추가'}
              </button>

              <div className="flex items-center justify-between gap-2 pt-2 border-t border-line">
                {/* 기존 override 수정일 때만 변경 여부를 안내 — override 신규 생성은 항상 저장 가능 */}
                <div className="text-xs text-ink-500">
                  {hasSiteOverride && (
                    <DirtyHint isDirty={isDirty} className="text-xs" />
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={handleCancelEdit}>
                    취소
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving || !isDirty}>
                    {isSaving ? '저장 중...' : '저장'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        )}
      </Card>

      {/* 외부 담당자 inline 생성 모달 */}
      {addContactForRow !== null && (
        <AddContactModal
          companyId={companyId}
          siteId={siteId}
          onCreated={(contact) => handleContactCreated(contact, addContactForRow)}
          onClose={() => setAddContactForRow(null)}
        />
      )}

      {/* 현장 override 되돌리기 확인 다이얼로그 — 되돌릴 수 없는 삭제라 danger(취소가 default) */}
      <ConfirmModal
        open={showResetConfirm}
        title="현장 결재선 되돌리기"
        body="현장 override를 삭제하고 양식 기본값을 적용합니다. 이 작업은 되돌릴 수 없습니다."
        confirmLabel={isResetting ? '처리 중...' : '되돌리기'}
        danger
        busy={isResetting}
        onConfirm={handleReset}
        onClose={() => setShowResetConfirm(false)}
      />
    </div>
  );
}
